import { screen } from 'components/styles/variables';
import Link from 'next/link';
import { useInView } from 'react-intersection-observer';
import classnames from 'classnames';
import { ArticleModel } from 'lib/Presenters/ArticleSummariesPresenter';
import { ReactNode } from 'react';
import articleThumbnailCSS from './styles/ArticleThumbnail';
import articlesTitleLinksCSS from './styles/ArticlesTitleLinks';

export function ArticleThumbnail({
  publicationDate,
  thumbImage,
  authorName,
  title,
  slug,
  categoryName,
  large,
  featured,
  parentPageSlug,
  onClick,
  engagementType,
}: ArticleThumbnailProps): JSX.Element {
  const [viewRef, inView] = useInView({ triggerOnce: true });
  return (
    <div className="article" ref={viewRef}>
      <ArticleLink
        parentPageSlug={parentPageSlug}
        slug={slug}
        onClick={onClick}
        engagementType={engagementType}
      >
        <article className={large ? 'lg' : ''}>
          <div className={classnames('thumb', { 'thumb-image-in-view': inView })} role="img" />
          <div className="data">
            <div className={`category ${featured ? 'featured' : ''}`}>{categoryName}</div>
            <div className="title">{title}</div>
            <div className="byline">
              {authorName} | {publicationDate}
            </div>
          </div>
        </article>
      </ArticleLink>
      <style jsx>{articleThumbnailCSS}</style>
      <style jsx>{`
        .thumb-image-in-view {
          background: url(${thumbImage});
          background-size: cover;
          background-position: center center;
        }
      `}</style>
    </div>
  );
}

function mapArticles(
  articleSummaries: ArticleModel[],
  onClick: (engagementType: string, targetPage: string) => void,
  engagementType: string
): JSX.Element[] {
  return articleSummaries.map((article) => (
    <ArticleThumbnail
      key={article.slug}
      {...article}
      onClick={onClick}
      engagementType={engagementType}
    />
  ));
}

interface ArticleSummariesProps {
  articleSummaries: ArticleModel[];
  onClick: (engagementType: string, targetPage: string) => void;
  engagementType: string;
}

function ArticleSummaries({
  articleSummaries,
  onClick,
  engagementType,
}: ArticleSummariesProps): JSX.Element {
  const firstThumbnailedArticleSummaries = articleSummaries.slice(0, 3);
  const secondThumbnailedArticleSummaries = articleSummaries.slice(3, 6);
  return (
    <div className="guides-articles">
      <div className="first-article-section">
        {mapArticles(firstThumbnailedArticleSummaries, onClick, engagementType)}
      </div>
      <div className="second-article-section">
        {mapArticles(secondThumbnailedArticleSummaries, onClick, engagementType)}
      </div>
      <style jsx>{`
        @media screen and (min-width: ${screen.mdMin}) {
          .guides-articles {
            display: flex;
          }

          .first-article-section {
            width: 50%;
          }

          .second-article-section {
            width: 50%;
            padding-left: 24px;
          }
        }
      `}</style>
    </div>
  );
}

interface ArticleThumbnailProps {
  publicationDate?: string;
  thumbImage?: string;
  authorName: string;
  title: string;
  categoryName: string;
  large?: boolean;
  featured?: boolean;
  parentPageSlug: string;
  slug: string;
  onClick: (...args: string[]) => void;
  engagementType: string;
}

interface ArticleLinkProps {
  parentPageSlug: string;
  slug: string;
  children: ReactNode;
  onClick: (...args: string[]) => void;
  engagementType: string;
}

export function ArticleLink({
  parentPageSlug,
  slug,
  children,
  onClick,
  engagementType,
}: ArticleLinkProps): JSX.Element {
  const articleUrl = `/${parentPageSlug}/${slug}`;

  return (
    <Link href={articleUrl} legacyBehavior>
      <a
        onClick={(): void => {
          onClick(engagementType, `${parentPageSlug}/${slug}`);
        }}
      >
        {children}
      </a>
    </Link>
  );
}

interface ComparisonLinkProps {
  slug: string;
  children: ReactNode;
  onClick: (engagementType: string, targetPage: string) => void;
  engagementType: string;
}

export function ComparisonLink({
  slug,
  children,
  onClick,
  engagementType,
}: ComparisonLinkProps): JSX.Element {
  return (
    <ArticleLink
      parentPageSlug={'comparisons'}
      slug={slug}
      onClick={onClick}
      engagementType={engagementType}
    >
      {children}
    </ArticleLink>
  );
}

interface ArticleTitleLinksProps {
  articles: ArticleModel[];
  onClick: (engagementType: string, targetPage: string) => void;
  engagementType: string;
}

export function makeArticleTitleLinks(
  TitleLinksComponent: typeof ArticleLink | typeof ComparisonLink
): (articleTitleLinksProps: ArticleTitleLinksProps) => JSX.Element {
  function component({ articles, onClick, engagementType }: ArticleTitleLinksProps): JSX.Element {
    return (
      <div className="links-container">
        {articles.map((article) => (
          <TitleLinksComponent
            onClick={onClick}
            engagementType={engagementType}
            key={article.slug}
            {...article}
          >
            <h3 className="article-title-link">{article.title}</h3>
          </TitleLinksComponent>
        ))}
        <style jsx>{articlesTitleLinksCSS}</style>
      </div>
    );
  }

  return component;
}

export default ArticleSummaries;
